import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Zap,
  PhoneIncoming,
  PhoneCall,
  CalendarCheck,
  MessageSquareQuote,
  PhoneForwarded,
  Play,
  Upload,
  CalendarSync,
  ArrowRight,
  User,
  Bot,
} from "lucide-react";
import AudioSampleCard, { Waveform } from "@/components/AudioSampleCard";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const EASE = [0.22, 1, 0.36, 1];

const metrics = [
  { value: "100%", label: "Calls Answered on Ring 1" },
  { value: "40+ Hours", label: "Front-Desk Saved / Month" },
  { value: "< 1.2s", label: "Voice Latency" },
  { value: "24/7/365", label: "Zero Missed Inquiries" },
];

const capabilities = [
  {
    icon: PhoneCall,
    title: "24/7 Inbound Answering",
    body: "Every call picked up instantly — nights, weekends and holidays included. No voicemail, no hold music, no missed revenue.",
  },
  {
    icon: CalendarCheck,
    title: "Smart Appointment Management",
    body: "Books, reschedules and cancels appointments straight into Google Calendar and Calendly, following your exact booking rules.",
  },
  {
    icon: MessageSquareQuote,
    title: "Instant Business FAQ Resolution",
    body: "Answers questions about hours, location, pricing and services with the knowledge you provide — accurately, every time.",
  },
  {
    icon: PhoneForwarded,
    title: "Intelligent Human Escalation",
    body: "Detects urgent or complex calls and warm-transfers to your team with a summary of the conversation so far.",
  },
];

const steps = [
  {
    icon: Upload,
    title: "Connect Knowledge",
    body: "Upload your FAQs, business hours and services. CogniTask learns to answer like your best front-desk hire.",
  },
  {
    icon: CalendarSync,
    title: "Sync Schedule",
    body: "Connect your calendar and define booking rules — slot lengths, buffers, provider preferences and blackout times.",
  },
  {
    icon: PhoneForwarded,
    title: "Forward Calls",
    body: "Keep your existing phone number. Simply forward calls when your line is busy or after hours — setup takes minutes.",
  },
];

const marqueeItems = [
  "24/7 Inbound Answering",
  "Zero Missed Calls",
  "< 1.2s Voice Latency",
  "Direct Calendar Booking",
  "Warm Human Escalation",
  "DPDP Act 2023 Compliant",
];

const heroLines = [
  { text: "Never Miss Another", accent: false },
  { text: "Customer Call. Meet Your", accent: false },
  { text: "24/7 AI Voice Receptionist.", accent: true },
];

function TiltCard({ children, className }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ transform: "perspective(1000px) rotateX(0deg) rotateY(0deg)" });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setStyle({
      transform: `perspective(1000px) rotateX(${(-py * 5).toFixed(2)}deg) rotateY(${(px * 7).toFixed(2)}deg)`,
      transition: "transform 0.08s ease-out",
    });
  };

  const onLeave = () =>
    setStyle({
      transform: "perspective(1000px) rotateX(0deg) rotateY(0deg)",
      transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
    });

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={style} className={className}>
      {children}
    </div>
  );
}

export default function Home() {
  const [answered, setAnswered] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const scrollToSamples = () => {
    const el = document.getElementById("audio-samples");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div
          aria-hidden="true"
          style={{
            y: blobY,
            background: "radial-gradient(closest-side, rgba(79,70,229,0.12), rgba(79,70,229,0.04), transparent)",
          }}
          className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pt-24">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              data-testid="hero-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-1.5 text-xs font-semibold text-indigo-700"
            >
              <Zap className="h-3.5 w-3.5" />
              24/7 AI Phone Receptionist
            </motion.span>

            <h1 className="mt-6 text-4xl font-bold leading-[1.15] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {heroLines.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "text-indigo-600" : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              CogniTask answers incoming phone calls instantly, answers customer questions, and books
              appointments straight into your calendar—so your front desk can focus on the room.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                to="/contact"
                data-testid="hero-demo-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700"
              >
                Book a 15-Minute Demo
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={scrollToSamples}
                data-testid="hero-listen-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:border-indigo-300 hover:text-indigo-700"
              >
                <Play className="h-4 w-4" />
                Listen to Live Sample
              </button>
            </motion.div>
          </div>

          {/* Hero visual: mock call card + audio simulator */}
          <motion.div style={{ y: cardY }} className="space-y-4" id="audio-samples">
            <motion.div
              initial={{ opacity: 0, y: 44, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.45, ease: EASE }}
            >
              <TiltCard>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md" data-testid="hero-call-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${answered ? "bg-emerald-100 pulse-ring" : "bg-indigo-100"}`}>
                        <PhoneIncoming className={`h-5 w-5 ${answered ? "text-emerald-600" : "text-indigo-600"}`} />
                      </span>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                          {answered ? "Call in progress" : "Incoming Call"}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">Sarah M. — Dental Cleaning</p>
                      </div>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                      Ring 1
                    </span>
                  </div>

                  <div className="mt-5 flex justify-center rounded-xl bg-slate-50 py-4">
                    <Waveform playing={answered} />
                  </div>

                  {answered ? (
                    <p className="mt-4 text-center text-sm font-medium text-emerald-600" data-testid="hero-call-answered-status">
                      CogniTask AI is answering and checking the calendar…
                    </p>
                  ) : (
                    <div className="mt-4 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setAnswered(true)}
                        data-testid="hero-call-accept-button"
                        className="flex-1 rounded-full bg-emerald-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                      >
                        Let AI Answer
                      </button>
                      <button
                        type="button"
                        data-testid="hero-call-decline-button"
                        className="flex-1 rounded-full bg-slate-100 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-200"
                      >
                        Send to Voicemail
                      </button>
                    </div>
                  )}
                </div>
              </TiltCard>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65, ease: EASE }}>
              <AudioSampleCard title="Clinic Booking Demo" duration={42} testid="audio-sample-clinic" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease: EASE }}>
              <AudioSampleCard title="Store Hours & Pricing" duration={42} testid="audio-sample-store" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* Metrics ribbon */}
      <section className="border-b border-slate-200 bg-white">
        <Reveal>
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px lg:grid-cols-4" data-testid="metrics-ribbon">
            {metrics.map((m) => (
              <div key={m.label} className="px-6 py-10 text-center transition-shadow duration-200 hover:shadow-md">
                <p className="text-3xl font-bold tracking-tight text-indigo-600 sm:text-4xl">{m.value}</p>
                <p className="mt-2 text-sm text-slate-500">{m.label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Capabilities */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">Core Capabilities</p>
          <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            A Front Desk That Never Sleeps
          </h2>
          <p className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-600" data-testid="capabilities-phone-line">
            <PhoneCall className="h-4 w-4 text-indigo-600" />
            Prefer to talk first? Call us at
            <a href="tel:+919891350540" className="font-semibold text-indigo-600 hover:text-indigo-700">+91 98913 50540</a>
          </p>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2" data-testid="capabilities-grid">
          {capabilities.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-shadow duration-200 hover:shadow-md">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
                  <c.icon className="h-6 w-6 text-indigo-600" />
                </span>
                <h3 className="mt-5 text-xl font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-24 border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">How It Works</p>
            <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Live on Your Phone Line in Three Steps
            </h2>
          </Reveal>
          <div className="relative mt-16 grid gap-10 lg:grid-cols-3" data-testid="how-it-works-steps">
            <div className="absolute left-0 right-0 top-8 hidden border-t-2 border-dashed border-slate-200 lg:block" aria-hidden="true" />
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="relative text-center lg:text-left">
                  <div className="relative z-10 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 shadow-sm lg:mx-0">
                    <s.icon className="h-7 w-7 text-indigo-600" />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Conversation preview */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">Live Conversation Preview</p>
          <h2 className="mx-auto mt-3 max-w-2xl text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Hear How Naturally It Handles Booking
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6 shadow-md sm:p-8" data-testid="conversation-preview">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100">
                <User className="h-4 w-4 text-slate-500" />
              </span>
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-5 py-3.5">
                <p className="text-xs font-semibold text-slate-400">Caller</p>
                <p className="mt-1 text-sm italic leading-relaxed text-slate-700">
                  "Hi, do you have any haircut slots available this Thursday afternoon?"
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-start justify-end gap-3">
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-indigo-600 px-5 py-3.5">
                <p className="text-xs font-semibold text-indigo-200">CogniTask AI</p>
                <p className="mt-1 text-sm italic leading-relaxed text-white">
                  "Let me check our calendar for Thursday. We have an opening at 2:30 PM and another at
                  4:15 PM with our senior stylist. Would either of those work for you?"
                </p>
              </div>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                <Bot className="h-4 w-4 text-indigo-600" />
              </span>
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 border-t border-slate-100 pt-5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <p className="text-xs text-slate-400">Booking confirmed to Google Calendar in real time</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Pre-footer CTA */}
      <section className="bg-slate-900" data-testid="home-cta-banner">
        <Reveal>
          <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Ready to eliminate missed calls?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">
              Hear a custom voice agent trained on your business in a 15-minute demo call.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-slate-900 shadow-sm transition-colors duration-200 hover:bg-indigo-50"
            >
              Book Your Demo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
